import * as React from "react"
import app from "gatsby-plugin-firebase-v9.0"
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  onSnapshot,
  getDocs,
  setDoc,
  getCountFromServer,
  deleteDoc,
} from "firebase/firestore"
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { User as FirebaseUser } from "firebase/auth"
import { User, MissingUser } from "./model"
import camera from "../../images/camera.svg"

const usersRef = () => collection(getFirestore(app), "users")
const PROFILE_PICTURES = "profile_pictures"
const BOOKMARKED_PROFILES = "bookmarkedProfiles"

export const fetchUser = async (
  uid: string,
  removeSensitive?: boolean,
): Promise<User | Partial<User>> => {
  // Fetch the data async
  const docSnapshot = await getDoc(doc(usersRef(), uid))
  // Unpack the data
  const user = docSnapshot.data() as User
  if (user) {
    user.id = docSnapshot.id
    user.profilePicUrl = user.profilePicUrl || camera
    if (typeof user.location !== "object") {
      user.location = { matching_full_name: user.location }
    }
    if (removeSensitive) {
      return {
        id: uid,
        firstName: user.firstName,
        lastName: user.lastName,
        headline: user.headline,
        location: user.location,
        profilePicUrl: user.profilePicUrl,
        interests: user.interests,
        description: user.description,
        openTo: user.openTo,
        availability: user.availability,
        deleted: user.deleted,
      }
    }
    return user
  }
  return { profilePicUrl: camera } as MissingUser
}

export const fetchUsers = async (
  uids: string[],
  removeSensitive?: boolean,
): Promise<Array<User | Partial<User> | MissingUser>> => {
  const users = await Promise.all(uids.map(uid => fetchUser(uid, removeSensitive)))
  return users
}

export const subscribeToUser = (
  firebaseUser: FirebaseUser,
  actionSuccess: (u: User) => void,
  actionError: (e: Error) => void,
) => {
  return onSnapshot(
    doc(usersRef(), firebaseUser.uid),
    async docSnapshot => {
      const userData = docSnapshot.data() as User
      if (!userData) {
        return
      }
      const update: Partial<User> = {}
      if (
        !userData.verified &&
        (firebaseUser.emailVerified ||
          !firebaseUser.providerData.map(provider => provider.providerId).includes("password"))
      ) {
        userData.verified = true
        update.verified = true
      }
      if (!userData.creationDate) {
        update.creationDate = Number(firebaseUser.metadata.createdAt)
      }
      if (Object.keys(update).length > 0) {
        await updateUserData(firebaseUser.uid, update)
      }
      const earlybirdMaxTime = (
        await getDoc(doc(collection(getFirestore(app), "codes"), "earlybird"))
      ).data()
      userData.earlybird =
        Number(firebaseUser.metadata.createdAt) < (earlybirdMaxTime ? earlybirdMaxTime.maxTime : 0)
      userData.onFreeTrial =
        Math.max(
          Number(firebaseUser.metadata.createdAt) + 30 * 24 * 3600 * 1000,
          new Date("2021-11").getTime(),
        ) > Date.now()
      actionSuccess && actionSuccess(userData)
    },
    actionError,
  )
}

export const subscribeAllUsers = (
  openTo: string[],
  actionSuccess: (newUsers: Partial<User>[]) => void,
  actionError?: (errorMessage: string) => void,
  options?: {
    includeAll?: boolean
    includeEmail?: boolean
  },
) => {
  let queryRef = openTo
    ? query(usersRef(), where("openTo", "array-contains-any", openTo))
    : usersRef()
  if (!options?.includeAll) {
    queryRef = query(queryRef, where("profileSearchable", "==", true))
  }
  return onSnapshot(
    queryRef,
    querySnapshot => {
      const newUsers: Partial<User>[] = []
      querySnapshot.forEach(doc => {
        const u = doc.data()
        if (u.deleted) return
        newUsers.push({
          id: doc.id,
          firstName: u.firstName,
          lastName: u.lastName,
          headline: u.headline,
          location: u.location,
          profilePicUrl: u.profilePicUrl,
          interests: u.interests,
          description: u.description,
          openTo: u.openTo,
          availability: u.availability,
          email: options && options.includeEmail && u.email,
          creationDate: u.creationDate,
          lastActive: u.lastActive,
          company: u.company,
          orgFunction: u.orgFunction,
          level: u.level,
        })
      })
      actionSuccess && actionSuccess(newUsers)
    },
    error => {
      actionError ? actionError(error.message) : console.log("subscribeAllUsers", error)
    },
  )
}

export const fetchAllUsers = async (includeEmail?: boolean) => {
  const docs = await getDocs(usersRef())
  const allUsers: Partial<User>[] = []
  docs.forEach(doc => {
    const u = doc.data()
    allUsers.push({
      id: doc.id,
      firstName: u.firstName,
      lastName: u.lastName,
      headline: u.headline,
      location: u.location,
      profilePicUrl: u.profilePicUrl,
      interests: u.interests,
      description: u.description,
      openTo: u.openTo,
      availability: u.availability,
      creationDate: u.creationDate,
      lastActive: u.lastActive,
      company: u.company,
      orgFunction: u.orgFunction,
      level: u.level,
      email: includeEmail ? u.email : undefined,
    })
  })
  return allUsers
}

export const updateUserData = async (uid: string, dataUpdate: Partial<User>) => {
  await updateDoc(doc(usersRef(), uid), dataUpdate)
}

export const checkUserExists = async (email: string): Promise<boolean> => {
  const queryRef = await getCountFromServer(query(usersRef(), where("email", "==", email)))
  return queryRef.data().count > 0
}

export const createUserData = async (
  uid: string,
  email: string,
  firstName: string,
  lastName: string,
  referralCode: string,
) => {
  const data = {
    id: uid,
    email,
    firstName,
    lastName,
    referralCode,
    verified: true,
    profileSearchable: true,
  }
  await setDoc(doc(usersRef(), uid), data)
}

export const getBookmarkedProfiles = async (
  uid: string,
): Promise<(User | Partial<User> | MissingUser)[]> => {
  const collectionRef = collection(getFirestore(app), usersRef().path, uid, BOOKMARKED_PROFILES)
  const collectionDocs = (await getDocs(collectionRef)).docs
  return fetchUsers(
    collectionDocs.map(doc => doc.id),
    true,
  )
}

export const bookmarkProfile = (
  uid: string,
  otherUid: string,
  bookmarked: boolean,
  actionEnd: () => void,
) => {
  const bookmarkedProfilesRef = collection(
    getFirestore(app),
    usersRef().path,
    uid,
    BOOKMARKED_PROFILES,
  )
  if (bookmarked) {
    deleteDoc(doc(bookmarkedProfilesRef, otherUid))
  } else {
    setDoc(doc(bookmarkedProfilesRef, otherUid), { bookmarkedAt: Date.now() })
  }
  actionEnd && actionEnd()
}

export const prioritiseUsers = (user: User, allUserData: User[]) => {
  const userSkills = user.skills ? Object.keys(user.skills) : []
  const userIndustries = user.industries ? Object.keys(user.industries) : []
  const userInterests = user.interests || []
  let scores
  if (
    !user.lookingFor ||
    user.lookingFor === "sameBackground" ||
    user.lookingFor === "noRequirement"
  ) {
    scores = allUserData.map(u => {
      const commonSkills = userSkills
        ? userSkills.filter(x => (u.skills ? new Set(Object.keys(u.skills)) : new Set()).has(x))
        : []
      const commonIndustries = userIndustries
        ? userIndustries.filter(x =>
            (u.industries ? new Set(Object.keys(u.industries)) : new Set()).has(x),
          )
        : []
      const commonInterests = userInterests
        ? userInterests.filter(x => (u.interests ? new Set(u.interests) : new Set()).has(x))
        : []
      const nMatches = commonSkills.length + commonIndustries.length + commonInterests.length
      const score = nMatches / (userSkills.length + userIndustries.length + userInterests.length)
      return score
    })
  } else {
    scores = allUserData.map(u => {
      let denominator = 0
      let relevantSkills: string[]
      let relevantIndustries: string[]
      let relevantInterests: string[]
      if (user.lookingForDetail.skills) {
        relevantSkills = user.lookingForDetail.skills.filter(x =>
          (u.skills ? new Set(Object.keys(u.skills)) : new Set()).has(x),
        )
        denominator += user.lookingForDetail.skills.length
      } else {
        relevantSkills = []
      }
      if (user.lookingForDetail.industries) {
        relevantIndustries = user.lookingForDetail.industries.filter(x =>
          (u.industries ? new Set(Object.keys(u.industries)) : new Set()).has(x),
        )
        denominator += user.lookingForDetail.industries.length
      } else {
        relevantIndustries = []
      }
      if (user.lookingForDetail.interests) {
        relevantInterests = user.lookingForDetail.interests.filter(x =>
          (u.interests ? new Set(u.interests) : new Set()).has(x),
        )
        denominator += user.lookingForDetail.interests.length
      } else {
        relevantInterests = []
      }
      const nMatches = relevantSkills.length + relevantIndustries.length + relevantInterests.length
      const score = nMatches / denominator
      return score
    })
  }

  return allUserData
    .slice()
    .sort((a, b) => scores[allUserData.indexOf(b)] - scores[allUserData.indexOf(a)])
}

export const setProfilePic = async (
  uid: string,
  image: Blob,
  actionSuccess?: (url: string) => void,
) => {
  const imageRef = ref(getStorage(app), `${PROFILE_PICTURES}/${uid}`)
  await uploadBytes(imageRef, image)
  const url = await getDownloadURL(imageRef)
  await updateUserData(uid, { profilePicUrl: url })
  actionSuccess && actionSuccess(url)
}

const concatParts = parts => {
  let arr = []
  const filteredParts = parts.filter(p => p)
  filteredParts.forEach((p, i) => {
    arr.push(p)
    if (i < filteredParts.length - 1) {
      if (i < filteredParts.length - 2) {
        arr.push(", ")
      } else {
        arr.push(" and ")
      }
    }
  })
  return arr
}

export const getSimilarities = (user1: Partial<User>, user2: Partial<User>) => {
  const commonSkills =
    user1.skills && user2.skills
      ? Object.keys(user1.skills).filter(value => Object.keys(user2.skills).includes(value))
      : []
  const commonIndustries =
    user1.industries && user2.industries
      ? Object.keys(user1.industries).filter(value => Object.keys(user2.industries).includes(value))
      : []
  const commonInterests =
    user1.interests && user2.interests
      ? user1.interests.filter(value => user2.interests.includes(value))
      : []
  let skillParts = null
  let industryParts = null
  let interestParts = null
  if (commonSkills.length) {
    skillParts = (
      <span>
        have{" "}
        {concatParts(
          commonSkills.map(x => (
            <span key={x} className="font-semibold">
              {x}
            </span>
          )),
        )}{" "}
        skills
      </span>
    )
  }
  if (commonIndustries.length) {
    industryParts = (
      <span>
        have worked in the{" "}
        {concatParts(
          commonIndustries.map(x => (
            <span key={x} className="font-semibold">
              {x}
            </span>
          )),
        )}{" "}
        industr{commonIndustries.length > 1 ? "ies" : "y"}
      </span>
    )
  }
  if (commonInterests.length) {
    interestParts = (
      <span>
        like{" "}
        {concatParts(
          commonInterests.map(x => (
            <span key={x} className="font-semibold">
              {x}
            </span>
          )),
        )}
      </span>
    )
  }
  if (skillParts || industryParts || interestParts) {
    return (
      <div>
        You and {user2.firstName} both {concatParts([skillParts, industryParts, interestParts])}.
      </div>
    )
  }
  return null
}
